/**
 * Super Admin only: sets a customer's matching limits. POST
 * /api/customers/{id}/matching-limits is append-only, so saving keeps the old
 * limits on record. Paused jobs resume as soon as a raised limit is saved.
 *
 * Budgets that together exceed the shared monthly unlock ceiling still save;
 * the backend says so in `warnings`, shown here before the editor closes.
 */

import { memo, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Space } from 'antd';
import axios from '../../../../axiosConfig';
import { MatchingLimits } from '../../types';
import styles from './index.module.css';

interface Props {
  customerId: number;
  current: MatchingLimits;
  onSaved: (saved: MatchingLimits) => void;
  onCancel: () => void;
}

interface Values {
  candidatesPerJob: number;
  monthlyJobCap: number;
  monthlyUnlockBudget: number;
  note?: string;
}

const MatchingLimitsEditor = ({
  customerId,
  current,
  onSaved,
  onCancel,
}: Props) => {
  const [form] = Form.useForm<Values>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saved, setSaved] = useState<MatchingLimits | null>(null);

  const submit = (values: Values) => {
    setSaving(true);
    setError(null);
    axios
      .post<MatchingLimits>(
        `/api/customers/${customerId}/matching-limits`,
        values,
      )
      .then((res) => {
        if (res.data.warnings && res.data.warnings.length > 0) {
          setWarnings(res.data.warnings);
          setSaved(res.data);
        } else {
          onSaved(res.data);
        }
      })
      .catch((err) => {
        setError(
          err?.response?.status === 400
            ? (err?.response?.data?.message ?? 'A limit is out of range.')
            : 'Could not save the limits. Try again.',
        );
      })
      .finally(() => setSaving(false));
  };

  if (saved) {
    return (
      <div className={styles.editor}>
        <Alert
          type="warning"
          showIcon
          message="Saved, with a warning"
          description={warnings.join(' ')}
        />
        <Button type="primary" onClick={() => onSaved(saved)}>
          Done
        </Button>
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      className={styles.editor}
      initialValues={{
        candidatesPerJob: current.candidatesPerJob,
        monthlyJobCap: current.monthlyJobCap,
        monthlyUnlockBudget: current.monthlyUnlockBudget ?? undefined,
      }}
      onFinish={submit}
    >
      <Form.Item
        name="candidatesPerJob"
        label="Candidates per job"
        rules={[
          {
            required: true,
            type: 'integer',
            min: 1,
            max: 100,
            message: '1 to 100',
          },
        ]}
      >
        <InputNumber min={1} max={100} precision={0} />
      </Form.Item>
      <Form.Item
        name="monthlyJobCap"
        label="Jobs matched per month"
        extra="0 pauses all new matching for this customer."
        rules={[
          { required: true, type: 'integer', min: 0, message: '0 or more' },
        ]}
      >
        <InputNumber min={0} precision={0} />
      </Form.Item>
      <Form.Item
        name="monthlyUnlockBudget"
        label="Monthly unlock budget (USD)"
        extra="Not shown to the customer, who sees it as a number of unlocks."
        rules={[
          { required: true, type: 'number', min: 0, message: '0 or more' },
        ]}
      >
        <InputNumber min={0} prefix="$" />
      </Form.Item>
      <Form.Item name="note" label="Note (why)">
        <Input
          maxLength={500}
          placeholder="e.g. Raised for October hiring push"
        />
      </Form.Item>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          className={styles.notice}
        />
      )}
      <Space>
        <Button type="primary" htmlType="submit" loading={saving}>
          Save limits
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </Space>
    </Form>
  );
};

export default memo(MatchingLimitsEditor);
